import type { Database, Json } from '@/types/database.generated';

export type JournalPersistIntent = 'draft' | 'finalize';

/** Live `create_journal_entry` argument names. No step id or extra fields. */
export const CREATE_JOURNAL_ENTRY_PARAM_NAMES = [
  'p_clinic_id',
  'p_patient_id',
  'p_entry_type',
  'p_title',
  'p_content_md',
  'p_content_structured',
  'p_entry_date',
  'p_appointment_id',
  'p_template_id',
  'p_content_html',
] as const;

export type CreateJournalEntryRpcArgs = Database['public']['Functions']['create_journal_entry']['Args'];

export type CreateJournalEntryInput = {
  clinicId: string;
  patientId: string;
  entryType?: Database['public']['Enums']['journal_entry_type'];
  title?: string;
  contentMd?: string;
  contentHtml?: string | null;
  contentStructured?: Json;
  entryDate?: string;
  appointmentId?: string;
  templateId?: string;
};

export type StepJournalNote = {
  id: string;
  appointment_id: string | null;
  discarded_at: string | null;
  finalized_at: string | null;
  row_version: number;
};

export type StepJournalPersistPlan =
  | { kind: 'create' }
  | { kind: 'update'; entryId: string; rowVersion: number }
  | { kind: 'finalized'; entryId: string };

/**
 * Named args for `create_journal_entry`. Omits empty optionals so PostgREST
 * uses the live defaults. Never sends a treatment-course step id.
 */
export function buildCreateJournalEntryRpcArgs(
  input: CreateJournalEntryInput,
): CreateJournalEntryRpcArgs {
  const args: CreateJournalEntryRpcArgs = {
    p_clinic_id: input.clinicId,
    p_patient_id: input.patientId,
    p_entry_type: input.entryType ?? 'generic',
    p_content_md: input.contentMd ?? '',
    p_content_structured: input.contentStructured ?? {},
  };
  if (input.title) args.p_title = input.title;
  if (input.contentHtml) args.p_content_html = input.contentHtml;
  if (input.entryDate) args.p_entry_date = input.entryDate;
  if (input.appointmentId) args.p_appointment_id = input.appointmentId;
  if (input.templateId) args.p_template_id = input.templateId;
  return args;
}

export function findStepJournalDraft(
  notes: StepJournalNote[],
  appointmentId: string,
): StepJournalNote | null {
  return (
    notes.find(
      (note) =>
        note.appointment_id === appointmentId &&
        !note.discarded_at &&
        !note.finalized_at,
    ) ?? null
  );
}

/**
 * One primary journal per appointment. A new step note creates. Continuing a
 * draft updates that row. A finalized primary cannot be created again.
 */
export function planStepJournalPersist(
  notes: StepJournalNote[],
  appointmentId: string,
): StepJournalPersistPlan {
  const draft = findStepJournalDraft(notes, appointmentId);
  if (draft) {
    return { kind: 'update', entryId: draft.id, rowVersion: draft.row_version };
  }
  const finalized = notes.find(
    (note) => note.appointment_id === appointmentId && !note.discarded_at,
  );
  if (finalized) {
    return { kind: 'finalized', entryId: finalized.id };
  }
  return { kind: 'create' };
}

export class JournalFinalizeAfterSaveError extends Error {
  readonly entryId: string;

  constructor(entryId: string, cause?: unknown) {
    super('finalize_failed');
    this.name = 'JournalFinalizeAfterSaveError';
    this.entryId = entryId;
    this.cause = cause;
  }
}

/**
 * Save first, then optionally finalize with the existing RPC.
 * Finalize is never called for 'draft'. If save succeeds and finalize fails,
 * throws JournalFinalizeAfterSaveError so the saved draft can stay visible.
 */
export async function persistJournalWithIntent<T extends { id: string }>(
  intent: JournalPersistIntent,
  save: () => Promise<T>,
  finalize: (entry: T) => Promise<unknown>,
): Promise<{ entry: T; finalized: boolean }> {
  const entry = await save();
  if (intent === 'draft') {
    return { entry, finalized: false };
  }
  try {
    await finalize(entry);
  } catch (error) {
    throw new JournalFinalizeAfterSaveError(entry.id, error);
  }
  return { entry, finalized: true };
}

export function canFinalizeJournalContent(content: string | null | undefined): boolean {
  return Boolean(content && content.trim());
}
